import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { TrendingDown, Users, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const EVENT_LABEL = {
  pricing_view: "Viewed Pricing",
  pricing_click: "Clicked a Tier",
  register_completed: "Created Account",
  checkout_initiated: "Started Checkout",
  checkout_completed: "Completed Checkout",
};

const TIER_COLOR = {
  pro: "#10B981",
  hustler: "#0EA5E9",
  team: "#6366F1",
  free: "#64748B",
  "—": "#94A3B8",
};

export default function Funnel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/analytics/funnel", { params: { days } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load funnel");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { refresh(); }, [refresh]);

  const maxTotal = data?.steps?.[0]?.total || 1;

  return (
    <div data-testid="funnel-page">
      <div className="topbar">
        <div>
          <div className="crumb">Analytics</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Conversion Funnel</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Real visitor → paying customer journey, last {days} days.
          </p>
        </div>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={"btn " + (days === d ? "btn-primary" : "btn-ghost")}
              data-testid={`funnel-range-${d}`}
            >
              {d}d
            </button>
          ))}
          <button type="button" onClick={refresh} className="btn btn-ghost" data-testid="funnel-refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin inline" /> Loading funnel…
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="funnel-steps">
          {data.steps.map((s) => {
            const widthPct = Math.max(8, Math.round((s.total / Math.max(maxTotal, 1)) * 100));
            const tierEntries = Object.entries(s.by_tier || {}).sort((a, b) => b[1] - a[1]);
            return (
              <div
                key={s.event}
                className="card p-4"
                data-testid={`funnel-step-${s.event}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">{s.event}</div>
                    <div className="font-display font-extrabold text-2xl">
                      {EVENT_LABEL[s.event] || s.event}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-3xl" data-testid={`funnel-count-${s.event}`}>{s.total.toLocaleString()}</div>
                    {s.conversion_from_previous_pct != null && s.conversion_from_previous_pct > 0 && (
                      <div className="text-xs text-[var(--ink-2)] flex items-center gap-1 justify-end">
                        {s.conversion_from_previous_pct.toFixed(1)}% of previous
                        {s.drop_off_pct > 0 && s.drop_off_pct < 100 && (
                          <span className="ml-1 text-rose-600 flex items-center gap-0.5">
                            <TrendingDown size={11} /> −{s.drop_off_pct.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    )}
                    {s.conversion_from_previous_pct === 0 && s.total === 0 && (
                      <div className="text-xs text-[var(--ink-3)] justify-end">No conversions yet</div>
                    )}
                  </div>
                </div>
                {/* the funnel bar */}
                <div className="h-3 rounded-full bg-slate-100 mt-3 overflow-hidden flex">
                  {tierEntries.length === 0 ? (
                    <div
                      className="h-full"
                      style={{ width: `${widthPct}%`, background: "#94A3B8" }}
                    />
                  ) : (
                    tierEntries.map(([tier, count]) => {
                      const tierPct = (count / Math.max(maxTotal, 1)) * 100;
                      return (
                        <div
                          key={tier}
                          className="h-full"
                          title={`${tier}: ${count}`}
                          style={{ width: `${tierPct}%`, background: TIER_COLOR[tier] || "#94A3B8" }}
                        />
                      );
                    })
                  )}
                </div>
                {tierEntries.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-[var(--ink-2)]">
                    {tierEntries.map(([tier, count]) => (
                      <span key={tier} className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: TIER_COLOR[tier] || "#94A3B8" }} />
                        {tier} · {count.toLocaleString()}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {data.steps.length > 0 && data.steps[0].total > 0 && (
            <div className="card p-4" data-testid="funnel-summary">
              <div className="flex items-center gap-2 text-sm font-semibold text-[var(--ink)]">
                <Users size={14} /> End-to-end conversion
              </div>
              <div className="font-display font-black text-3xl mt-1">
                {((data.steps[data.steps.length - 1].total / Math.max(data.steps[0].total, 1)) * 100).toFixed(2)}%
              </div>
              <div className="text-xs text-[var(--ink-2)] mt-1">
                visitor → paying customer in the last {days} days
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
